import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { CreditCard, MessageSquare, Phone, Eye, EyeOff, Check } from "lucide-react";
import { toast } from "sonner";

const PAY_PROVIDERS = [
  { v: "razorpay", label: "Razorpay" },
  { v: "instamojo", label: "Instamojo" },
  { v: "ccavenue", label: "CCAvenue" },
];
const SMS_PROVIDERS = [
  { v: "msg91", label: "MSG91" },
  { v: "fast2sms", label: "Fast2SMS" },
  { v: "generic", label: "Generic / Other" },
];
const WA_PROVIDERS = [
  { v: "meta_cloud_api", label: "Meta Cloud API" },
  { v: "wati", label: "WATI" },
  { v: "interakt", label: "Interakt" },
];

const MASKED = (v) => typeof v === "string" && v.includes("•");
const clone = (o) => JSON.parse(JSON.stringify(o));

function SecretInput({ id, value, onChange, show, placeholder }) {
  return (
    <Input id={id} type={show ? "text" : "password"} value={value ?? ""} placeholder={placeholder}
      autoComplete="off" spellCheck={false} className="font-mono text-sm"
      onChange={(e) => onChange(e.target.value)} data-testid={id} />
  );
}

function TextField({ id, label, value, onChange, placeholder, hint }) {
  return (
    <div>
      <Label className="text-xs" htmlFor={id}>{label}</Label>
      <Input id={id} data-testid={id} className="mt-1 font-mono text-sm" placeholder={placeholder}
        value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
      {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

export default function GatewaySettings() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/institution/integrations")
      .then((r) => { setData(r.data); setForm(clone(r.data)); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setPay = (key) => (val) => setForm((f) => ({ ...f, payment: { ...f.payment, [key]: val } }));
  const setPaySub = (sub, key) => (val) =>
    setForm((f) => ({ ...f, payment: { ...f.payment, [sub]: { ...f.payment[sub], [key]: val } } }));
  const setField = (section, key) => (val) =>
    setForm((f) => ({ ...f, [section]: { ...f[section], [key]: val } }));

  // send only what actually changed; a still-masked secret is never re-sent
  const buildPayload = () => {
    const out = {};
    const p = form.payment, op = data.payment;
    const pDiff = {};
    if (p.provider !== op.provider) pDiff.provider = p.provider;
    if (p.enabled !== op.enabled) pDiff.enabled = p.enabled;
    for (const sub of ["razorpay", "instamojo", "ccavenue"]) {
      const sd = {};
      for (const [k, v] of Object.entries(p[sub] || {})) {
        if (v === (op[sub] || {})[k]) continue;
        if (typeof v === "string" && MASKED(v)) continue;
        sd[k] = v;
      }
      if (Object.keys(sd).length) pDiff[sub] = sd;
    }
    if (Object.keys(pDiff).length) out.payment = pDiff;

    for (const section of ["sms", "whatsapp"]) {
      const cur = form[section] || {}, orig = data[section] || {};
      const sec = {};
      for (const [k, v] of Object.entries(cur)) {
        if (k === "configured") continue;
        if (v === orig[k]) continue;
        if (typeof v === "string" && MASKED(v)) continue;
        sec[k] = v;
      }
      if (Object.keys(sec).length) out[section] = sec;
    }
    return out;
  };

  const save = async () => {
    const payload = buildPayload();
    if (!Object.keys(payload).length) { toast.info("No changes to save"); return; }
    setSaving(true);
    try {
      const { data: fresh } = await api.put("/admin/institution/integrations", payload);
      setData(fresh);
      setForm(clone(fresh));
      toast.success("Integration settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return <div className="py-16 flex justify-center"><div className="w-6 h-6 border-2 border-slate-300 border-t-slate-800 rounded-full animate-spin" /></div>;
  }

  const pay = form.payment, sm = form.sms, wa = form.whatsapp;
  const prov = pay.provider || "razorpay";

  return (
    <div data-testid="gateway-settings" className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          Your own gateway credentials — stored per institution. Secrets are masked after saving and never leave the server in full.
        </p>
        <Button variant="outline" size="sm" data-testid="gateway-toggle-visibility" onClick={() => setShow((s) => !s)}>
          {show ? <EyeOff className="w-4 h-4 mr-1.5" /> : <Eye className="w-4 h-4 mr-1.5" />}
          {show ? "Hide keys" : "Show keys"}
        </Button>
      </div>

      {/* Payment gateway (polymorphic) */}
      <Card className="p-5 border-slate-200 space-y-4" data-testid="gateway-payment">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-lg bg-blue-50 grid place-items-center"><CreditCard className="w-5 h-5 text-blue-600" /></span>
            <div>
              <h3 className="font-semibold text-slate-800">Payment Gateway Provider</h3>
              <p className="text-xs text-slate-400">Checkout for the public application fee.</p>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-slate-500">{pay.enabled ? "Enabled" : "Disabled"}</span>
            <Switch checked={!!pay.enabled} onCheckedChange={setPay("enabled")} data-testid="payment-enabled" />
          </label>
        </div>

        <div className="max-w-xs">
          <Label className="text-xs">Provider</Label>
          <Select value={prov} onValueChange={setPay("provider")}>
            <SelectTrigger className="mt-1" data-testid="payment-provider"><SelectValue /></SelectTrigger>
            <SelectContent>{PAY_PROVIDERS.map((p) => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {prov === "razorpay" && (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="payment-razorpay">
            <TextField id="razorpay-key_id" label="Key ID" placeholder="rzp_live_XXXXXXXXXXXX"
              value={pay.razorpay.key_id} onChange={setPaySub("razorpay", "key_id")} />
            <div>
              <Label className="text-xs" htmlFor="razorpay-key_secret">Key Secret</Label>
              <div className="mt-1"><SecretInput id="razorpay-key_secret" show={show} value={pay.razorpay.key_secret}
                onChange={setPaySub("razorpay", "key_secret")} placeholder="•••• (unchanged)" /></div>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs" htmlFor="razorpay-webhook_secret">Webhook Secret</Label>
              <div className="mt-1"><SecretInput id="razorpay-webhook_secret" show={show} value={pay.razorpay.webhook_secret}
                onChange={setPaySub("razorpay", "webhook_secret")} placeholder="•••• (unchanged)" /></div>
            </div>
          </div>
        )}

        {prov === "instamojo" && (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="payment-instamojo">
            <TextField id="instamojo-client_id" label="Client ID" placeholder="Instamojo API v2 Client ID"
              value={pay.instamojo.client_id} onChange={setPaySub("instamojo", "client_id")} />
            <div>
              <Label className="text-xs" htmlFor="instamojo-client_secret">Client Secret</Label>
              <div className="mt-1"><SecretInput id="instamojo-client_secret" show={show} value={pay.instamojo.client_secret}
                onChange={setPaySub("instamojo", "client_secret")} placeholder="•••• (unchanged)" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <Switch checked={!!pay.instamojo.is_sandbox} onCheckedChange={setPaySub("instamojo", "is_sandbox")} data-testid="instamojo-sandbox" />
              <span className="text-slate-600">Use the Instamojo <span className="font-mono">test</span> environment</span>
            </label>
          </div>
        )}

        {prov === "ccavenue" && (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="payment-ccavenue">
            <TextField id="ccavenue-merchant_id" label="Merchant ID" placeholder="1234567"
              value={pay.ccavenue.merchant_id} onChange={setPaySub("ccavenue", "merchant_id")} />
            <TextField id="ccavenue-access_code" label="Access Code" placeholder="AVXXXXXXXXXXXXXXXXXX"
              value={pay.ccavenue.access_code} onChange={setPaySub("ccavenue", "access_code")} />
            <div className="sm:col-span-2">
              <Label className="text-xs" htmlFor="ccavenue-working_key">Working Key</Label>
              <div className="mt-1"><SecretInput id="ccavenue-working_key" show={show} value={pay.ccavenue.working_key}
                onChange={setPaySub("ccavenue", "working_key")} placeholder="•••• (unchanged)" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <Switch checked={!!pay.ccavenue.is_sandbox} onCheckedChange={setPaySub("ccavenue", "is_sandbox")} data-testid="ccavenue-sandbox" />
              <span className="text-slate-600">Use the CCAvenue <span className="font-mono">test</span> environment</span>
            </label>
          </div>
        )}
      </Card>

      {/* SMS */}
      <Card className="p-5 border-slate-200 space-y-4" data-testid="gateway-sms">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-lg bg-indigo-50 grid place-items-center"><Phone className="w-5 h-5 text-indigo-600" /></span>
            <div>
              <h3 className="font-semibold text-slate-800">SMS Gateway</h3>
              <p className="text-xs text-slate-400">Transactional SMS for lead & payment events.</p>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-slate-500">{sm.enabled ? "Enabled" : "Disabled"}</span>
            <Switch checked={!!sm.enabled} onCheckedChange={setField("sms", "enabled")} data-testid="sms-enabled" />
          </label>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Provider</Label>
            <Select value={sm.provider || "msg91"} onValueChange={setField("sms", "provider")}>
              <SelectTrigger className="mt-1" data-testid="sms-provider"><SelectValue /></SelectTrigger>
              <SelectContent>{SMS_PROVIDERS.map((p) => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs" htmlFor="sms-api_key">Auth Key</Label>
            <div className="mt-1"><SecretInput id="sms-api_key" show={show} value={sm.api_key}
              onChange={setField("sms", "api_key")} placeholder="•••• (unchanged)" /></div>
          </div>
          <TextField id="sms-sender_id" label="DLT Sender ID" placeholder="AURORA"
            value={sm.sender_id} onChange={setField("sms", "sender_id")} />
          <TextField id="sms-dlt_entity_id" label="DLT Entity ID" placeholder="1101XXXXXXXXXXXXXXX"
            value={sm.dlt_entity_id} onChange={setField("sms", "dlt_entity_id")} />
        </div>
      </Card>

      {/* WhatsApp */}
      <Card className="p-5 border-slate-200 space-y-4" data-testid="gateway-whatsapp">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-lg bg-green-50 grid place-items-center"><MessageSquare className="w-5 h-5 text-green-600" /></span>
            <div>
              <h3 className="font-semibold text-slate-800">WhatsApp Business API</h3>
              <p className="text-xs text-slate-400">Rich messaging for the same events.</p>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-slate-500">{wa.enabled ? "Enabled" : "Disabled"}</span>
            <Switch checked={!!wa.enabled} onCheckedChange={setField("whatsapp", "enabled")} data-testid="whatsapp-enabled" />
          </label>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Provider</Label>
            <Select value={wa.provider || "meta_cloud_api"} onValueChange={setField("whatsapp", "provider")}>
              <SelectTrigger className="mt-1" data-testid="whatsapp-provider"><SelectValue /></SelectTrigger>
              <SelectContent>{WA_PROVIDERS.map((p) => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs" htmlFor="whatsapp-api_key">API Key / Access Token</Label>
            <div className="mt-1"><SecretInput id="whatsapp-api_key" show={show} value={wa.api_key}
              onChange={setField("whatsapp", "api_key")} placeholder="•••• (unchanged)" /></div>
          </div>
          <div className="sm:col-span-2">
            <TextField id="whatsapp-phone_number_id" label="Account Phone Number ID" placeholder="1099999999999999"
              value={wa.phone_number_id} onChange={setField("whatsapp", "phone_number_id")} />
          </div>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button className="bg-brand" data-testid="gateway-save" disabled={saving} onClick={save}>
          <Check className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}
